import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '$store';
import Loader from '~components/generic/loader/Loader';
import { DuplicateModalController } from '~extension/web2print/controller/DuplicateModalController';
import useMediaLibrarySearchQuery from '~modules/media-library/context/useMediaLibrarySearchQuery';
import useMediaLibrarySettings from '~modules/media-library/context/useMediaLibrarySettings';
import useMediaLibraryUserPreferences from '~modules/media-library/context/useMediaLibraryUserPreferences';
import { ApprovalFormModalController } from '~modules/media-library/controller/ApprovalFormModalController';
import { CanvaModalController } from '~modules/media-library/controller/CanvaModalController';
import { ConfirmDeleteModalController } from '~modules/media-library/controller/ConfirmDeleteModalController';
import { CropModalController } from '~modules/media-library/controller/CropModalController';
import { DetailModalController } from '~modules/media-library/controller/DetailModalController';
import { EditAssetsModalController } from '~modules/media-library/controller/EditAssetsModalController';
import { JsmContactFormModalController } from '~modules/media-library/controller/JsmContactFormModalController';
import { RaiseRequestModalController } from '~modules/media-library/controller/RaiseRequestModalController';
import ResultHeaderController from '~modules/media-library/controller/ResultHeaderController';
import ResultListController from '~modules/media-library/controller/ResultListController';
import SearchHeaderController from '~modules/media-library/controller/SearchHeaderController';
import { ShareModalController } from '~modules/media-library/controller/ShareModalController';
import useAssetActions from '~modules/media-library/controller/useAssetActions';
import { ZoomModalController } from '~modules/media-library/controller/ZoomModalController';
import { CollectionHeader } from '~modules/media-library/view/CollectionHeader/CollectionHeader';
import { ContributeConfirmCancelModal } from '~modules/media-library/view/Contribute/ContributeConfirmCancelModal/ContributeConfirmCancelModal';
import { LoadingError } from '~modules/media-library/view/LoadingError';
import { ShareAction } from '~modules/media-library/view/ShareModal/ShareAction';

type MediaLibraryProps = {
  realm: string;
};

export function MediaLibrary(_props: MediaLibraryProps) {
  const { isError, isLoading } = useMediaLibrarySettings();
  const {
    viewVariant,
    selectedFilterValueIds,
    selectedTabFilterId,
    sortKey,
    sortOrder,
    showFavouritesOnly,
    preferencesChanged,
  } = useMediaLibraryUserPreferences();
  const { searchTerm } = useMediaLibrarySearchQuery();
  const contributeData = useSelector((store: RootState) => store.contribute);
  const { contributionMode } = contributeData;
  const ref = useRef<HTMLDivElement>(null);

  const {
    idDetails,
    idDetailsNext,
    idDetailsPrev,
    idDownload,
    closeDetails,
    closeDownload,
    idZoom,
    closeZoom,
    idCrop,
    idApprovalForm,
    idDelete,
    closeCrop,
    idAddToMyfiles,
    closeAddToMyfiles,
    idWeb2Print,
    idCanva,
    idRaiseRequestForm,
    idJsmContactForm,
    closeWeb2Print,
    closeCanva,
    closeRaiseRequest,
    closeApprovalForm,
    closeConfirmDelete,
    onDeleteSuccess,
    closeContributionWizard,
    closeJsmContactForm,
    ...AssetActions
  } = useAssetActions();

  useEffect(() => {
    if (Boolean(ref.current) && preferencesChanged) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
  }, [
    searchTerm,
    viewVariant,
    selectedFilterValueIds,
    selectedTabFilterId,
    sortKey,
    sortOrder,
    showFavouritesOnly,
    ref,
    preferencesChanged,
  ]);

  return (
    <div className="ml--module-view bg-gray w-full min-h-screen " ref={ref}>
      {isError || isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          {isError && <LoadingError />}
          {isLoading && (
            <div className="relative w-16 h-8">
              <Loader absoluteCenter={true} />
            </div>
          )}
        </div>
      ) : (
        <>
          <CollectionHeader />
          <div className="ml--sticky-header sticky top-0 z-20 flex flex-col">
            <SearchHeaderController />
            <ResultHeaderController />
          </div>
          <ResultListController AssetActions={AssetActions} />
        </>
      )}
      <DetailModalController
        assetId={idDetails}
        onClose={closeDetails}
        AssetActions={AssetActions}
        nextAssetId={idDetailsNext}
        prevAssetId={idDetailsPrev}
      />
      <ShareModalController assetId={idDownload} onClose={closeDownload} shareAction={ShareAction.Download} />
      <ShareModalController
        assetId={idAddToMyfiles}
        onClose={closeAddToMyfiles}
        shareAction={ShareAction.AddToMyfiles}
      />
      <ZoomModalController assetId={idZoom} onClose={closeZoom} />
      <CropModalController assetId={idCrop} onClose={closeCrop} />
      <DuplicateModalController assetId={idWeb2Print} onClose={closeWeb2Print} />
      <CanvaModalController assetId={idCanva} onClose={closeCanva} />
      <RaiseRequestModalController assetId={idRaiseRequestForm} onClose={closeRaiseRequest} />
      <EditAssetsModalController isOpen={Boolean(contributionMode)} onClose={closeContributionWizard} />
      <ApprovalFormModalController assetId={idApprovalForm} onClose={closeApprovalForm} />
      <ConfirmDeleteModalController assetId={idDelete} onClose={closeConfirmDelete} onDeleteSuccess={onDeleteSuccess} />
      <ContributeConfirmCancelModal />
      <JsmContactFormModalController assetId={idJsmContactForm} onClose={closeJsmContactForm} />
    </div>
  );
}
